(function () {
  'use strict';

  const theme = window.Montelier || {};
  const routes = theme.routes || {};
  const scrim = document.querySelector('[data-drawer-scrim]');
  const mainContent = document.querySelector('.main-content');
  let siteHeader = document.querySelector('[data-site-header]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activeDrawer = null;
  let returnFocusTo = null;
  let scrimHideTimer = null;
  let revealObserver = null;
  let cartToastTimer = null;
  let cartToastReturnFocus = null;

  const escapeHtml = (value) => {
    const element = document.createElement('div');
    element.textContent = value == null ? '' : String(value);
    return element.innerHTML;
  };

  const formatMoney = (cents, format) => {
    const template = format || theme.moneyFormat || '${{amount}}';
    const amount = Number(cents || 0) / 100;
    const tokens = {
      amount: amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      amount_no_decimals: Math.round(amount).toLocaleString(),
      amount_with_comma_separator: amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      amount_no_decimals_with_comma_separator: Math.round(amount).toLocaleString('de-DE'),
      amount_with_apostrophe_separator: amount.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    };
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => tokens[token] || tokens.amount);
  };

  const setPageLoading = () => {
    if (mainContent) mainContent.classList.add('is-loading');
  };

  const syncHeaderHeight = () => {
    if (!siteHeader) return;
    document.documentElement.style.setProperty('--header-height', `${Math.ceil(siteHeader.getBoundingClientRect().height)}px`);
  };

  let headerFrame = null;
  const updateHeader = () => {
    headerFrame = null;
    siteHeader = document.querySelector('[data-site-header]');
    if (!siteHeader) return;
    if (!siteHeader.classList.contains('site-header--static')) {
      siteHeader.classList.toggle('is-condensed', window.scrollY > 36);
    }
    syncHeaderHeight();
  };
  const requestHeaderUpdate = () => {
    if (headerFrame) return;
    headerFrame = window.requestAnimationFrame(updateHeader);
  };
  updateHeader();
  window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
  window.addEventListener('resize', requestHeaderUpdate, { passive: true });
  document.addEventListener('transitionend', (event) => {
    if (event.target.closest && event.target.closest('[data-site-header]')) syncHeaderHeight();
  });

  const revealElements = (root = document) => {
    const elements = [];
    if (root.matches && root.matches('[data-reveal]')) elements.push(root);
    if (root.querySelectorAll) elements.push(...root.querySelectorAll('[data-reveal]'));
    if (!elements.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) {
      elements.forEach((element) => element.classList.add('is-revealed'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    elements.forEach((element) => revealObserver.observe(element));
  };

  revealElements();
  document.addEventListener('shopify:section:load', (event) => {
    revealElements(event.target);
    updateHeader();
  });
  window.addEventListener('pageshow', () => {
    if (mainContent) mainContent.classList.remove('is-loading');
    const visibleDrawer = document.querySelector('[data-menu-drawer].is-open, [data-search-dialog].is-open, [data-support-dialog].is-open');
    if (!visibleDrawer) {
      document.body.classList.remove('drawer-open');
      document.documentElement.style.removeProperty('--drawer-scrollbar-width');
      document.body.style.removeProperty('padding-inline-end');
      document.body.style.removeProperty('padding-right');
    }
    syncHeaderHeight();
  });

  const cartToast = document.querySelector('[data-cart-toast]');
  const cartToastTitle = cartToast && cartToast.querySelector('[data-cart-toast-title]');
  const statusAnnouncer = document.createElement('p');
  statusAnnouncer.className = 'visually-hidden';
  statusAnnouncer.setAttribute('role', 'status');
  statusAnnouncer.setAttribute('aria-live', 'polite');
  document.body.appendChild(statusAnnouncer);
  const announceStatus = (message) => {
    statusAnnouncer.textContent = '';
    window.requestAnimationFrame(() => { statusAnnouncer.textContent = message; });
  };
  const hideCartToast = () => {
    if (!cartToast) return;
    window.clearTimeout(cartToastTimer);
    const focusWasInside = cartToast.contains(document.activeElement);
    cartToast.classList.remove('is-visible');
    cartToast.setAttribute('aria-hidden', 'true');
    cartToast.setAttribute('inert', '');
    if (focusWasInside && cartToastReturnFocus && cartToastReturnFocus.isConnected) cartToastReturnFocus.focus();
    cartToastReturnFocus = null;
  };
  const showCartToast = (productTitle, trigger) => {
    if (!cartToast) return;
    window.clearTimeout(cartToastTimer);
    cartToastReturnFocus = trigger || document.activeElement;
    if (cartToastTitle) cartToastTitle.textContent = productTitle
      ? `${productTitle} added to your bag`
      : 'Added to your bag';
    cartToast.removeAttribute('inert');
    cartToast.setAttribute('aria-hidden', 'false');
    window.requestAnimationFrame(() => cartToast.classList.add('is-visible'));
    cartToastTimer = window.setTimeout(hideCartToast, 4200);
  };
  if (cartToast) {
    const closeButton = cartToast.querySelector('[data-cart-toast-close]');
    if (closeButton) closeButton.addEventListener('click', hideCartToast);
    cartToast.addEventListener('focusin', () => window.clearTimeout(cartToastTimer));
    cartToast.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (cartToast.classList.contains('is-visible') && !cartToast.contains(document.activeElement)) {
          cartToastTimer = window.setTimeout(hideCartToast, 1800);
        }
      }, 0);
    });
  }

  const getFocusable = (container) => Array.from(container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), details > summary, [tabindex]:not([tabindex="-1"])'
  )).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

  const supportsModalDialog = (element) => element
    && element.tagName === 'DIALOG'
    && typeof element.showModal === 'function';


  const focusWithoutScroll = (element) => {
    if (!element) return;
    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  };

  const lockPageScroll = () => {
    if (document.body.classList.contains('drawer-open')) return;
    document.body.classList.add('drawer-open');
  };

  const unlockPageScroll = () => {
    document.body.classList.remove('drawer-open');
    document.documentElement.style.removeProperty('--drawer-scrollbar-width');
    document.body.style.removeProperty('padding-inline-end');
    document.body.style.removeProperty('padding-right');
  };

  // Search intentionally has no full-page scrim. Besides keeping the page bright,
  // this prevents a transparent overlay outside the header stacking context from
  // intercepting clicks after the drawer has finished opening.
  const drawerUsesScrim = (drawer) => Boolean(
    scrim
    && drawer
    && !supportsModalDialog(drawer)
    && !drawer.matches('[data-search-dialog], [data-support-dialog]')
  );

  let accountReturnFocusTo = null;
  let accountDropdownHideTimer = null;

  const getAccountDropdown = () => document.querySelector('[data-account-dropdown]');

  const closeAccountDropdown = (restoreFocus = true) => {
    const dropdown = getAccountDropdown();
    if (!dropdown || !dropdown.classList.contains('is-open')) return;
    dropdown.classList.remove('is-open');
    dropdown.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-account-toggle]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    window.clearTimeout(accountDropdownHideTimer);
    accountDropdownHideTimer = window.setTimeout(() => dropdown.setAttribute('inert', ''), reducedMotion.matches ? 0 : 360);
    if (restoreFocus && accountReturnFocusTo && accountReturnFocusTo.isConnected) focusWithoutScroll(accountReturnFocusTo);
    accountReturnFocusTo = null;
  };

  const openAccountDropdown = (trigger) => {
    const dropdown = getAccountDropdown();
    if (!dropdown) return;
    closeCartDropdown(false);
    if (activeFilterPopover) closeFilterPopover(false);
    if (activeDrawer) closeDrawer(activeDrawer, false);
    accountReturnFocusTo = trigger || document.activeElement;
    window.clearTimeout(accountDropdownHideTimer);
    dropdown.removeAttribute('inert');
    dropdown.setAttribute('aria-hidden', 'false');
    dropdown.getBoundingClientRect();
    dropdown.classList.add('is-open');
    document.querySelectorAll('[data-account-toggle]').forEach((accountTrigger) => accountTrigger.setAttribute('aria-expanded', 'true'));
    window.setTimeout(() => {
      const closeButton = dropdown.querySelector('[data-account-close]');
      if (closeButton) focusWithoutScroll(closeButton);
    }, reducedMotion.matches ? 0 : 180);
  };

  let activeFilterPopover = null;
  let activeFilterTrigger = null;
  let filterReturnFocusTo = null;
  let filterHideTimer = null;

  const positionFilterPopover = (popover = activeFilterPopover, trigger = activeFilterTrigger) => {
    if (!popover || !trigger || !trigger.isConnected) return;

    const viewportPadding = window.innerWidth < 750 ? 12 : 18;
    const gap = window.innerWidth < 750 ? 10 : 12;
    const triggerRect = trigger.getBoundingClientRect();
    const popoverWidth = Math.min(window.innerWidth - (viewportPadding * 2), window.innerWidth < 750 ? 360 : 410);
    const availableBelow = window.innerHeight - triggerRect.bottom - gap - viewportPadding;
    const availableAbove = triggerRect.top - gap - viewportPadding;
    const useTopPlacement = availableBelow < 300 && availableAbove > availableBelow;
    const maxHeight = Math.max(220, Math.min(620, useTopPlacement ? availableAbove : availableBelow));
    const left = Math.min(
      Math.max(viewportPadding, triggerRect.left),
      window.innerWidth - popoverWidth - viewportPadding
    );
    const top = useTopPlacement
      ? Math.max(viewportPadding, triggerRect.top - gap - maxHeight)
      : triggerRect.bottom + gap;

    popover.dataset.placement = useTopPlacement ? 'top' : 'bottom';
    popover.style.setProperty('--filter-popover-top', `${Math.round(top)}px`);
    popover.style.setProperty('--filter-popover-left', `${Math.round(left)}px`);
    popover.style.setProperty('--filter-popover-width', `${Math.round(popoverWidth)}px`);
    popover.style.setProperty('--filter-popover-max-height', `${Math.round(maxHeight)}px`);
  };

  const closeFilterPopover = (restoreFocus = true) => {
    const popover = activeFilterPopover;
    if (!popover) return;

    popover.classList.remove('is-open');
    popover.setAttribute('aria-hidden', 'true');
    if (activeFilterTrigger) activeFilterTrigger.setAttribute('aria-expanded', 'false');
    window.clearTimeout(filterHideTimer);
    filterHideTimer = window.setTimeout(() => {
      popover.setAttribute('inert', '');
      popover.removeAttribute('data-placement');
    }, reducedMotion.matches ? 0 : 280);

    const focusTarget = filterReturnFocusTo;
    activeFilterPopover = null;
    activeFilterTrigger = null;
    filterReturnFocusTo = null;
    if (restoreFocus && focusTarget && focusTarget.isConnected) focusWithoutScroll(focusTarget);
  };

  const openFilterPopover = (popover, trigger) => {
    if (!popover || !trigger) return;
    closeAccountDropdown(false);
    closeCartDropdown(false);
    if (activeDrawer) closeDrawer(activeDrawer, false);
    if (activeFilterPopover && activeFilterPopover !== popover) closeFilterPopover(false);

    activeFilterPopover = popover;
    activeFilterTrigger = trigger;
    filterReturnFocusTo = trigger;
    window.clearTimeout(filterHideTimer);
    popover.removeAttribute('inert');
    popover.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    positionFilterPopover(popover, trigger);
    popover.getBoundingClientRect();
    popover.classList.add('is-open');

    window.setTimeout(() => {
      const firstFocusable = getFocusable(popover)[0];
      if (firstFocusable) focusWithoutScroll(firstFocusable);
    }, reducedMotion.matches ? 0 : 170);
  };

  const updateFilterPopoverPosition = () => {
    if (activeFilterPopover) positionFilterPopover();
  };

  window.addEventListener('resize', updateFilterPopoverPosition, { passive: true });
  window.addEventListener('scroll', updateFilterPopoverPosition, { passive: true });

  let cartReturnFocusTo = null;
  let cartDropdownHideTimer = null;
  let cartMutationPending = false;

  const getCartDropdown = () => document.querySelector('[data-cart-dropdown]');

  const closeCartDropdown = (restoreFocus = true) => {
    const dropdown = getCartDropdown();
    if (!dropdown || !dropdown.classList.contains('is-open')) return;
    dropdown.classList.remove('is-open');
    dropdown.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-cart-toggle]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    window.clearTimeout(cartDropdownHideTimer);
    cartDropdownHideTimer = window.setTimeout(() => dropdown.setAttribute('inert', ''), 420);
    if (restoreFocus && cartReturnFocusTo && cartReturnFocusTo.isConnected) focusWithoutScroll(cartReturnFocusTo);
    cartReturnFocusTo = null;
  };

  const openCartDropdown = (trigger, refresh = true) => {
    const dropdown = getCartDropdown();
    if (!dropdown) return;
    closeAccountDropdown(false);
    if (activeFilterPopover) closeFilterPopover(false);
    if (activeDrawer) closeDrawer(activeDrawer, false);
    cartReturnFocusTo = trigger || document.activeElement;
    window.clearTimeout(cartDropdownHideTimer);
    dropdown.removeAttribute('inert');
    dropdown.setAttribute('aria-hidden', 'false');
    dropdown.getBoundingClientRect();
    dropdown.classList.add('is-open');
    document.querySelectorAll('[data-cart-toggle]').forEach((cartTrigger) => cartTrigger.setAttribute('aria-expanded', 'true'));
    if (refresh) refreshCartCount({ showLoading: true });
    window.setTimeout(() => {
      const closeButton = dropdown.querySelector('[data-cart-close]');
      focusWithoutScroll(closeButton);
    }, 220);
  };

  const trapTabKey = (event) => {
    if (event.key !== 'Tab' || !activeDrawer) return;
    const focusable = getFocusable(activeDrawer);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openDrawer = (drawer, trigger) => {
    if (!drawer) return;
    closeAccountDropdown(false);
    closeCartDropdown(false);
    if (activeFilterPopover) closeFilterPopover(false);
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(activeDrawer, false);
    activeDrawer = drawer;
    returnFocusTo = trigger || document.activeElement;
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    if (drawer.tagName === 'DIALOG' && !drawer.open) {
      if (supportsModalDialog(drawer)) drawer.showModal();
      else drawer.setAttribute('open', '');
    }
    drawer.getBoundingClientRect();
    drawer.classList.add('is-open');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      if (trigger.dataset.closeLabel) trigger.setAttribute('aria-label', trigger.dataset.closeLabel);
    }
    lockPageScroll();
    if (drawerUsesScrim(drawer)) {
      window.clearTimeout(scrimHideTimer);
      scrim.hidden = false;
      requestAnimationFrame(() => scrim.classList.add('is-active'));
    } else if (scrim) {
      window.clearTimeout(scrimHideTimer);
      scrim.classList.remove('is-active');
      scrim.hidden = true;
    }
    if (!drawer.matches('[data-search-dialog]')) {
      const focusable = getFocusable(drawer);
      if (focusable[0]) requestAnimationFrame(() => focusWithoutScroll(focusable[0]));
    }
  };

  const closeDrawer = (drawer = activeDrawer, restoreFocus = true) => {
    if (!drawer) return;
    const usesScrim = drawerUsesScrim(drawer);
    drawer.classList.remove('is-open');
    if (drawer.tagName === 'DIALOG' && drawer.open) {
      if (typeof drawer.close === 'function') drawer.close();
      else drawer.removeAttribute('open');
    }
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      if (trigger.dataset.openLabel) trigger.setAttribute('aria-label', trigger.dataset.openLabel);
    });
    if (scrim && usesScrim) {
      scrim.classList.remove('is-active');
      window.clearTimeout(scrimHideTimer);
      scrimHideTimer = window.setTimeout(() => { scrim.hidden = true; }, 220);
    }
    unlockPageScroll();
    activeDrawer = null;
    if (restoreFocus && returnFocusTo) focusWithoutScroll(returnFocusTo);
    returnFocusTo = null;
  };

  document.addEventListener('keydown', (event) => {
    const accountDropdown = getAccountDropdown();
    const cartDropdown = getCartDropdown();
    if (event.key === 'Escape' && accountDropdown && accountDropdown.classList.contains('is-open')) {
      closeAccountDropdown();
      return;
    }
    if (event.key === 'Escape' && activeFilterPopover) {
      closeFilterPopover();
      return;
    }
    if (event.key === 'Escape' && cartDropdown && cartDropdown.classList.contains('is-open')) {
      closeCartDropdown();
      return;
    }
    if (event.key === 'Escape' && activeDrawer) closeDrawer();

    const target = event.target;
    const isTyping = target && (target.matches('input, textarea, select') || target.isContentEditable);
    if (event.key === '/' && !activeDrawer && !activeFilterPopover && !isTyping && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const searchTrigger = document.querySelector('[data-search-open]');
      if (searchTrigger) {
        event.preventDefault();
        const header = searchTrigger.closest('[data-site-header]');
        openSearch((header && header.querySelector('[data-search-dialog]')) || document.querySelector('[data-search-dialog]'), searchTrigger);
      }
    }

    trapTabKey(event);
  });

  if (scrim) {
    scrim.addEventListener('click', () => {
      // Keep search open until the customer explicitly closes it or submits.
      if (activeDrawer && activeDrawer.matches('[data-search-dialog]')) return;
      closeDrawer();
    });
  }

  const controlledElement = (trigger, fallbackSelector) => {
    const controlledId = trigger && trigger.getAttribute('aria-controls');
    return (controlledId && document.getElementById(controlledId)) || document.querySelector(fallbackSelector);
  };

  const openSearch = (searchDialog, trigger) => {
    if (!searchDialog) return;
    const searchReturnTarget = activeDrawer
      ? document.querySelector(`[aria-controls="${activeDrawer.id}"]`)
      : trigger;
    if (activeDrawer) closeDrawer(activeDrawer, false);
    openDrawer(searchDialog, trigger);
    returnFocusTo = searchReturnTarget || trigger;
    const searchInput = searchDialog.querySelector('[data-predictive-search-input]');
    renderRecentSearches(searchDialog);
    syncSearchDrawer(searchInput);
    window.setTimeout(() => focusWithoutScroll(searchInput), 150);
  };

  const closeSearch = (searchDialog) => {
    closeDrawer(searchDialog);
  };

  document.addEventListener('click', (event) => {
    const accountToggle = event.target.closest('[data-account-toggle]');
    if (accountToggle) {
      const dropdown = getAccountDropdown();
      if (dropdown && dropdown.classList.contains('is-open')) closeAccountDropdown();
      else openAccountDropdown(accountToggle);
      return;
    }

    const accountClose = event.target.closest('[data-account-close]');
    if (accountClose) {
      closeAccountDropdown();
      return;
    }

    const cartToggle = event.target.closest('[data-cart-toggle]');
    if (cartToggle) {
      const dropdown = getCartDropdown();
      if (dropdown && dropdown.classList.contains('is-open')) closeCartDropdown();
      else openCartDropdown(cartToggle);
      return;
    }

    const cartClose = event.target.closest('[data-cart-close]');
    if (cartClose) {
      closeCartDropdown();
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity-action]');
    if (quantityButton) {
      const row = quantityButton.closest('[data-cart-dropdown-item]');
      const currentQuantity = Number(row && row.dataset.lineQuantity || 0);
      const action = quantityButton.dataset.cartQuantityAction;
      const nextQuantity = Math.max(0, currentQuantity + (action === 'increase' ? 1 : -1));
      changeCartLine(quantityButton.dataset.lineKey, nextQuantity, action);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      changeCartLine(removeButton.dataset.lineKey, 0, 'remove');
      return;
    }

    const menuToggle = event.target.closest('[data-menu-toggle]');
    if (menuToggle) {
      const drawer = controlledElement(menuToggle, '[data-menu-drawer]');
      if (drawer && drawer.classList.contains('is-open')) closeDrawer(drawer);
      else openDrawer(drawer, menuToggle);
      return;
    }

    const menuClose = event.target.closest('[data-menu-close]');
    if (menuClose) {
      closeDrawer(menuClose.closest('[data-menu-drawer]'));
      return;
    }

    const supportOpen = event.target.closest('[data-support-open]');
    if (supportOpen) {
      const header = supportOpen.closest('[data-site-header]');
      const supportDialog = (header && header.querySelector('[data-support-dialog]')) || document.querySelector('[data-support-dialog]');
      if (supportDialog && supportDialog.classList.contains('is-open')) closeDrawer(supportDialog);
      else openDrawer(supportDialog, supportOpen);
      return;
    }

    const supportClose = event.target.closest('[data-support-close]');
    if (supportClose) {
      closeDrawer(supportClose.closest('[data-support-dialog]'));
      return;
    }

    const supportFormJump = event.target.closest('[data-support-form-jump]');
    if (supportFormJump) {
      const supportPanel = supportFormJump.closest('[data-support-dialog]');
      const supportForm = supportPanel && supportPanel.querySelector('[data-support-form]');
      if (supportForm) {
        supportForm.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
        window.setTimeout(() => {
          const firstField = supportForm.querySelector('input:not([type="hidden"]), textarea, button');
          if (firstField) firstField.focus({ preventScroll: true });
        }, reducedMotion.matches ? 0 : 420);
      }
      return;
    }

    const copySupportEmail = event.target.closest('[data-copy-support-email]');
    if (copySupportEmail) {
      const email = copySupportEmail.dataset.email || 'montelier.support@gmail.com';
      const supportPanel = copySupportEmail.closest('[data-support-dialog]');
      const status = supportPanel && supportPanel.querySelector('[data-support-status]');
      const showCopied = () => {
        copySupportEmail.textContent = 'Email copied';
        if (status) status.textContent = `${email} copied to your clipboard.`;
        window.setTimeout(() => { copySupportEmail.textContent = 'Copy support email'; }, 1800);
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(email).then(showCopied).catch(() => {
          const input = document.createElement('textarea');
          input.value = email;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
          showCopied();
        });
      } else {
        const input = document.createElement('textarea');
        input.value = email;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
        showCopied();
      }
      return;
    }

    const filterOpen = event.target.closest('[data-filter-open]');
    if (filterOpen) {
      const popover = controlledElement(filterOpen, '[data-filter-drawer]');
      if (popover && popover.classList.contains('is-open')) closeFilterPopover();
      else openFilterPopover(popover, filterOpen);
      return;
    }

    const filterClose = event.target.closest('[data-filter-close]');
    if (filterClose) {
      closeFilterPopover();
      return;
    }

    if (activeFilterPopover) {
      if (event.target.closest('[data-filter-drawer]')) return;
      closeFilterPopover(false);
    }

    const searchOpen = event.target.closest('[data-search-open]');
    if (searchOpen) {
      const header = searchOpen.closest('[data-site-header]');
      openSearch((header && header.querySelector('[data-search-dialog]')) || document.querySelector('[data-search-dialog]'), searchOpen);
      return;
    }

    const searchClose = event.target.closest('[data-search-close]');
    if (searchClose) {
      closeSearch(searchClose.closest('[data-search-dialog]'));
      return;
    }

    // Keep clicks inside the support center from falling through to unrelated
    // global drawer handlers.
    if (event.target.closest('[data-support-dialog]')) return;

    // Do not let a click inside search fall through to unrelated global drawer
    // handlers. Separate delegated search actions still receive the same event.
    if (event.target.closest('[data-search-dialog]')) return;

    const menuDrawer = event.target.closest('[data-menu-drawer]');
    if (menuDrawer && (event.target === menuDrawer || event.target.closest('a[href]'))) {
      closeDrawer(menuDrawer, event.target === menuDrawer);
      return;
    }

    // Clicking inside the search panel—including its empty padding—must not close it.
    // Search closes only from the close button, Escape key, or page navigation.

    const openAccountDropdownElement = getAccountDropdown();
    if (openAccountDropdownElement
      && openAccountDropdownElement.classList.contains('is-open')
      && !openAccountDropdownElement.contains(event.target)) {
      closeAccountDropdown(false);
    }

    const openCartDropdownElement = getCartDropdown();
    if (openCartDropdownElement
      && openCartDropdownElement.classList.contains('is-open')
      && !openCartDropdownElement.contains(event.target)) {
      closeCartDropdown(false);
    }
  });

  document.addEventListener('cancel', (event) => {
    const menuDrawer = event.target.closest && event.target.closest('[data-menu-drawer]');
    if (menuDrawer) {
      event.preventDefault();
      closeDrawer(menuDrawer);
      return;
    }
    const searchDialog = event.target.closest && event.target.closest('[data-search-dialog]');
    if (searchDialog) {
      event.preventDefault();
      closeSearch(searchDialog);
      return;
    }
    const supportDialog = event.target.closest && event.target.closest('[data-support-dialog]');
    if (supportDialog) {
      event.preventDefault();
      closeDrawer(supportDialog);
    }
  }, true);

  document.addEventListener('toggle', (event) => {
    const details = event.target.closest && event.target.closest('[data-menu-details]');
    if (!details || !details.open) return;
    const menuDrawer = details.closest('[data-menu-drawer]');
    if (!menuDrawer) return;
    menuDrawer.querySelectorAll('[data-menu-details][open]').forEach((otherDetails) => {
      if (otherDetails !== details) otherDetails.open = false;
    });
  }, true);

  let predictiveTimer;
  let predictiveController;
  const recentSearchKey = 'montelierRecentSearches';

  const getRecentSearches = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(recentSearchKey) || '[]');
      return Array.isArray(stored) ? stored.filter((item) => typeof item === 'string').slice(0, 4) : [];
    } catch (error) {
      return [];
    }
  };

  const saveRecentSearch = (query) => {
    const cleanQuery = String(query || '').trim();
    if (cleanQuery.length < 2) return;
    try {
      const recent = getRecentSearches().filter((item) => item.toLowerCase() !== cleanQuery.toLowerCase());
      recent.unshift(cleanQuery);
      window.localStorage.setItem(recentSearchKey, JSON.stringify(recent.slice(0, 4)));
    } catch (error) {
      // Search still works when storage is unavailable.
    }
  };

  const renderRecentSearches = (searchDrawer) => {
    if (!searchDrawer) return;
    const wrapper = searchDrawer.querySelector('[data-recent-searches-wrap]');
    const container = searchDrawer.querySelector('[data-recent-searches]');
    if (!wrapper || !container) return;
    const recent = getRecentSearches();
    wrapper.hidden = !recent.length;
    container.innerHTML = recent.map((query) => (
      `<button type="button" data-search-suggestion="${escapeHtml(query)}">${escapeHtml(query)}<span aria-hidden="true">&#8635;</span></button>`
    )).join('');
  };

  const syncSearchDrawer = (searchInput) => {
    if (!searchInput) return;
    const searchDrawer = searchInput.closest('[data-search-dialog]');
    if (!searchDrawer) return;
    const query = searchInput.value.trim();
    const clearButton = searchDrawer.querySelector('[data-search-clear]');
    const discovery = searchDrawer.querySelector('[data-search-discovery]');
    if (clearButton) clearButton.hidden = !query;
    if (discovery) discovery.hidden = Boolean(query);
    searchDrawer.dataset.searchState = query ? 'active' : 'idle';
  };

  const runDrawerSearch = (searchInput, immediate = false, remember = false) => {
    if (!searchInput) return;
    const searchDrawer = searchInput.closest('[data-search-dialog]');
    const resultsContainer = searchDrawer && searchDrawer.querySelector('[data-predictive-results]');
    if (!resultsContainer) return;

    window.clearTimeout(predictiveTimer);
    const query = searchInput.value.trim();
    syncSearchDrawer(searchInput);

    if (query.length < 2) {
      if (predictiveController) predictiveController.abort();
      resultsContainer.innerHTML = query.length
        ? '<p class="predictive-results__message">Add one more character and the collection will begin to appear.</p>'
        : '';
      return;
    }

    const performSearch = async () => {
      if (predictiveController) predictiveController.abort();
      predictiveController = new AbortController();
      searchDrawer.dataset.searchState = 'loading';
      resultsContainer.innerHTML = `
        <div class="predictive-results__loading" aria-label="Searching">
          <span class="predictive-results__skeleton"></span>
          <span class="predictive-results__skeleton"></span>
          <span class="predictive-results__skeleton"></span>
        </div>
      `;

      try {
        const url = `${routes.predictiveSearch}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
        const response = await fetch(url, { signal: predictiveController.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        const products = data.resources && data.resources.results ? data.resources.results.products || [] : [];
        searchDrawer.dataset.searchState = products.length ? 'results' : 'empty';

        if (remember) {
          saveRecentSearch(query);
          renderRecentSearches(searchDrawer);
        }

        if (!products.length) {
          const browseUrl = routes.allProducts || routes.collections || '/collections/all';
          resultsContainer.innerHTML = `
            <div class="predictive-results__message">
              No pieces found for <strong>${escapeHtml(query)}</strong>.<br>
              <a href="${escapeHtml(browseUrl)}">Explore all collections <span aria-hidden="true">&#8599;</span></a>
            </div>
          `;
          return;
        }

        const items = products.map((product, index) => {
          const imageUrl = product.featured_image && product.featured_image.url
            ? `${product.featured_image.url}${product.featured_image.url.includes('?') ? '&' : '?'}width=120`
            : '';
          const image = imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="" width="54" height="54" loading="lazy">` : '';
          const rawPrice = Number(product.price);
          const price = Number.isFinite(rawPrice) ? formatMoney(Math.round(rawPrice * 100)) : '';
          return `<li class="predictive-results__item" style="--result-index:${index};"><a href="${escapeHtml(product.url)}">${image}<strong>${escapeHtml(product.title)}</strong>${price ? `<span>${price}</span>` : ''}</a></li>`;
        }).join('');

        const allResultsUrl = `${routes.search}?q=${encodeURIComponent(query)}`;
        resultsContainer.innerHTML = `
          <p class="predictive-results__summary">${products.length} ${products.length === 1 ? 'piece' : 'pieces'} found for <strong>${escapeHtml(query)}</strong></p>
          <ul class="predictive-results__list" role="list">${items}</ul>
          <a class="predictive-results__all" href="${escapeHtml(allResultsUrl)}">View all results <span aria-hidden="true">&#8599;</span></a>
        `;
      } catch (error) {
        if (error.name !== 'AbortError') {
          searchDrawer.dataset.searchState = 'error';
          resultsContainer.innerHTML = '<p class="predictive-results__message">Search is temporarily unavailable. Please try again in a moment.</p>';
        }
      }
    };

    if (immediate) performSearch();
    else predictiveTimer = window.setTimeout(performSearch, 220);
  };

  document.addEventListener('submit', (event) => {
    const supportForm = event.target.closest && event.target.closest('[data-support-form]');
    if (supportForm) {
      const requiredSelect = supportForm.querySelector('[data-luxury-select-required]');
      if (requiredSelect && !requiredSelect.value) {
        event.preventDefault();
        const menu = requiredSelect.closest('[data-luxury-select]');
        const trigger = menu && menu.querySelector('[data-luxury-select-trigger]');
        const status = supportForm.querySelector('[data-support-status]');
        if (menu) {
          menu.classList.add('is-invalid');
          menu.setAttribute('open', '');
        }
        if (status) status.textContent = 'Please choose a support topic.';
        if (trigger) trigger.focus({ preventScroll: true });
        return;
      }
      if (!supportForm.reportValidity()) {
        event.preventDefault();
        return;
      }

      const submitButton = supportForm.querySelector('[data-support-submit]');
      const status = supportForm.querySelector('[data-support-status]');
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.firstChild.textContent = 'Sending request ';
      }
      if (status) status.textContent = 'Sending your request securely…';
      return;
    }

    const form = event.target.closest && event.target.closest('[data-search-drawer-form]');
    if (!form) return;

    const searchInput = form.querySelector('[data-predictive-search-input]');
    const query = searchInput ? searchInput.value.trim() : '';
    if (!query) {
      event.preventDefault();
      if (searchInput) searchInput.focus();
      return;
    }

    // Preserve recent searches, then allow the native GET form to open
    // Shopify's complete /search results page.
    saveRecentSearch(query);
    setPageLoading();
  });

  const submittedSupportResult = document.querySelector('[data-support-form-result]');
  if (submittedSupportResult) {
    const supportDialog = submittedSupportResult.closest('[data-support-dialog]');
    const supportTrigger = document.querySelector(`[aria-controls="${supportDialog && supportDialog.id}"]`);
    if (supportDialog) {
      window.requestAnimationFrame(() => {
        openDrawer(supportDialog, supportTrigger || null);
        const target = supportDialog.querySelector('[data-support-success], .support-request-form__errors');
        if (target) {
          target.scrollIntoView({ behavior: 'auto', block: 'center' });
          if (target.hasAttribute('tabindex')) target.focus({ preventScroll: true });
        }
      });
    }
  }

  document.addEventListener('input', (event) => {
    const searchInput = event.target.closest('[data-predictive-search-input]');
    if (!searchInput) return;
    runDrawerSearch(searchInput);
  });

  document.addEventListener('click', (event) => {
    const suggestion = event.target.closest('[data-search-suggestion]');
    if (suggestion) {
      const searchDrawer = suggestion.closest('[data-search-dialog]');
      const searchInput = searchDrawer && searchDrawer.querySelector('[data-predictive-search-input]');
      if (!searchInput) return;
      searchInput.value = suggestion.dataset.searchSuggestion || suggestion.textContent.trim();
      syncSearchDrawer(searchInput);
      runDrawerSearch(searchInput, true, true);
      searchInput.focus();
      return;
    }

    const clearButton = event.target.closest('[data-search-clear]');
    if (clearButton) {
      const searchDrawer = clearButton.closest('[data-search-dialog]');
      const searchInput = searchDrawer && searchDrawer.querySelector('[data-predictive-search-input]');
      const resultsContainer = searchDrawer && searchDrawer.querySelector('[data-predictive-results]');
      if (predictiveController) predictiveController.abort();
      window.clearTimeout(predictiveTimer);
      if (searchInput) searchInput.value = '';
      if (resultsContainer) resultsContainer.innerHTML = '';
      renderRecentSearches(searchDrawer);
      syncSearchDrawer(searchInput);
      if (searchInput) searchInput.focus();
      return;
    }

    const resultLink = event.target.closest('[data-search-dialog] [data-predictive-results] a[href]');
    if (resultLink) {
      const searchDrawer = resultLink.closest('[data-search-dialog]');
      const searchInput = searchDrawer && searchDrawer.querySelector('[data-predictive-search-input]');
      if (searchInput) saveRecentSearch(searchInput.value);
    }
  });

  const closeLuxurySelectMenus = (except = null) => {
    document.querySelectorAll('[data-luxury-select][open]').forEach((menu) => {
      if (menu !== except) menu.removeAttribute('open');
    });
  };

  document.addEventListener('toggle', (event) => {
    const menu = event.target.closest && event.target.closest('[data-luxury-select]');
    if (!menu || !menu.open) return;
    closeLuxurySelectMenus(menu);
    document.querySelectorAll('[data-sort-menu][open]').forEach((sortMenu) => sortMenu.removeAttribute('open'));
  }, true);

  document.addEventListener('click', (event) => {
    const option = event.target.closest('[data-luxury-select-option]');
    if (option) {
      if (option.disabled || option.getAttribute('aria-disabled') === 'true') return;
      const menu = option.closest('[data-luxury-select]');
      const select = menu && menu.querySelector('[data-luxury-select-native]');
      const current = menu && menu.querySelector('[data-luxury-select-current]');
      if (!menu || !select) return;

      select.value = option.dataset.selectValue || '';
      if (current) {
        const label = option.querySelector('span');
        current.textContent = label ? label.textContent.trim() : option.textContent.trim();
      }
      menu.querySelectorAll('[data-luxury-select-option]').forEach((item) => {
        const selected = item === option;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
      menu.classList.remove('is-invalid');
      menu.removeAttribute('open');
      const formStatus = menu.closest('form') && menu.closest('form').querySelector('[data-support-status]');
      if (formStatus && formStatus.textContent === 'Please choose a support topic.') formStatus.textContent = '';
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (!event.target.closest('[data-luxury-select]')) closeLuxurySelectMenus();
  });

  document.addEventListener('keydown', (event) => {
    const menu = event.target.closest && event.target.closest('[data-luxury-select]');
    if (!menu) return;
    const trigger = event.target.closest('[data-luxury-select-trigger]');
    const options = Array.from(menu.querySelectorAll('[data-luxury-select-option]:not([disabled])'));

    if (trigger && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      menu.setAttribute('open', '');
      const selected = menu.querySelector('[data-luxury-select-option].is-selected:not([disabled])');
      const target = selected || (event.key === 'ArrowUp' ? options[options.length - 1] : options[0]);
      if (target) target.focus({ preventScroll: true });
      return;
    }

    if (!menu.open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      menu.removeAttribute('open');
      const menuTrigger = menu.querySelector('[data-luxury-select-trigger]');
      if (menuTrigger) menuTrigger.focus({ preventScroll: true });
      return;
    }

    const option = event.target.closest('[data-luxury-select-option]');
    if (!option || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || !options.length) return;
    const index = options.indexOf(option);
    let nextIndex = index;
    if (event.key === 'ArrowDown') nextIndex = (index + 1) % options.length;
    if (event.key === 'ArrowUp') nextIndex = (index - 1 + options.length) % options.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = options.length - 1;
    event.preventDefault();
    options[nextIndex].focus({ preventScroll: true });
  });

  const closeLuxurySortMenus = (except = null) => {
    document.querySelectorAll('[data-sort-menu][open]').forEach((menu) => {
      if (menu !== except) menu.removeAttribute('open');
    });
  };

  document.addEventListener('toggle', (event) => {
    const menu = event.target.closest && event.target.closest('[data-sort-menu]');
    if (menu && menu.open) {
      closeLuxurySortMenus(menu);
      closeLuxurySelectMenus();
    }
  }, true);

  document.addEventListener('click', (event) => {
    const option = event.target.closest('[data-sort-option]');
    if (option) {
      const menu = option.closest('[data-sort-menu]');
      const select = menu && menu.querySelector('[data-sort-select]');
      const current = menu && menu.querySelector('[data-sort-current]');
      if (!menu || !select) return;

      select.value = option.dataset.sortValue || '';
      if (current) current.textContent = option.textContent.trim();
      menu.querySelectorAll('[data-sort-option]').forEach((item) => {
        const selected = item === option;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
      menu.removeAttribute('open');
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (!event.target.closest('[data-sort-menu]')) closeLuxurySortMenus();
  });

  document.addEventListener('keydown', (event) => {
    const menu = event.target.closest && event.target.closest('[data-sort-menu]');
    if (!menu || !menu.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      menu.removeAttribute('open');
      const trigger = menu.querySelector('summary');
      if (trigger) trigger.focus({ preventScroll: true });
      return;
    }

    const option = event.target.closest('[data-sort-option]');
    if (!option || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const options = Array.from(menu.querySelectorAll('[data-sort-option]'));
    const index = options.indexOf(option);
    let nextIndex = index;
    if (event.key === 'ArrowDown') nextIndex = (index + 1) % options.length;
    if (event.key === 'ArrowUp') nextIndex = (index - 1 + options.length) % options.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = options.length - 1;
    event.preventDefault();
    options[nextIndex].focus({ preventScroll: true });
  });

  document.addEventListener('change', (event) => {
    const sortSelect = event.target.closest('[data-sort-select]');
    if (sortSelect) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      setPageLoading();
      window.location.assign(url.toString());
      return;
    }

    const form = event.target.closest('[data-facets-form]');
    if (form && event.target.matches('input[type="checkbox"]')) {
      setPageLoading();
      form.submit();
    }
  });

  document.addEventListener('submit', (event) => {
    if (event.target.matches('[data-facets-form]')) setPageLoading();
  });

  document.addEventListener('click', (event) => {
    const minus = event.target.closest('[data-quantity-minus]');
    const plus = event.target.closest('[data-quantity-plus]');
    const button = minus || plus;
    if (!button) return;
    const wrapper = button.closest('.quantity-selector');
    const input = wrapper && wrapper.querySelector('[data-quantity-input]');
    if (!input) return;
    const minimum = Number(input.min || 0);
    const current = Number(input.value || minimum);
    input.value = String(Math.max(minimum, current + (plus ? 1 : -1)));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  const cartEmptyMarkup = () => `
    <div class="cart-dropdown__empty">
      <span class="cart-dropdown__empty-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M5 8h14l-1 13H6L5 8Z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></svg>
      </span>
      <h3>Your bag is waiting.</h3>
      <p>Discover a piece made to mark the moments that matter.</p>
      <a class="button button--navy" href="${escapeHtml(routes.allProducts || routes.collections || '/collections/all')}">Explore collections</a>
    </div>`;

  const cartItemMarkup = (item, index) => {
    const itemUrl = escapeHtml(item.url || '#');
    const title = escapeHtml(item.product_title || item.title || 'Montelier piece');
    const lineKey = escapeHtml(item.key || '');
    const quantity = Number(item.quantity || 0);
    const image = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${title}" width="90" height="98" loading="lazy">`
      : '<span aria-hidden="true"></span>';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-dropdown__variant">${escapeHtml(item.variant_title)}</p>`
      : '';
    return `
      <article class="cart-dropdown__item" role="listitem" style="--cart-item-index: ${index}" data-cart-dropdown-item data-line-key="${lineKey}" data-line-quantity="${quantity}">
        <a class="cart-dropdown__image" href="${itemUrl}" tabindex="-1" aria-hidden="true">${image}</a>
        <div class="cart-dropdown__details">
          <div>
            <div class="cart-dropdown__item-top">
              <a class="cart-dropdown__title" href="${itemUrl}">${title}</a>
              <strong>${escapeHtml(formatMoney(item.final_line_price))}</strong>
            </div>
            ${variant}
          </div>
          <div class="cart-dropdown__item-actions">
            <div class="cart-dropdown__quantity" aria-label="Quantity for ${title}">
              <button type="button" aria-label="Decrease quantity of ${title}" data-cart-quantity-action="decrease" data-line-key="${lineKey}">&minus;</button>
              <span aria-live="polite">${quantity}</span>
              <button type="button" aria-label="Increase quantity of ${title}" data-cart-quantity-action="increase" data-line-key="${lineKey}">+</button>
            </div>
            <button class="cart-dropdown__remove" type="button" data-cart-remove data-line-key="${lineKey}">Remove</button>
          </div>
        </div>
      </article>`;
  };

  const showCartError = (message) => {
    const dropdown = getCartDropdown();
    const errorElement = dropdown && dropdown.querySelector('[data-cart-error]');
    if (!errorElement) return;
    errorElement.textContent = message || '';
    errorElement.hidden = !message;
  };

  const setCartBusy = (busy) => {
    const dropdown = getCartDropdown();
    if (!dropdown) return;
    dropdown.classList.toggle('is-updating', busy);
    if (busy) dropdown.setAttribute('aria-busy', 'true');
    else dropdown.removeAttribute('aria-busy');
    dropdown.querySelectorAll('[data-cart-quantity-action], [data-cart-remove]').forEach((control) => {
      control.disabled = busy;
    });
  };

  const renderCartDropdown = (cart) => {
    const dropdown = getCartDropdown();
    if (!dropdown || !cart) return;
    const body = dropdown.querySelector('[data-cart-dropdown-body]');
    const footer = dropdown.querySelector('[data-cart-footer]');
    const subtotal = dropdown.querySelector('[data-cart-subtotal]');
    const headingCount = dropdown.querySelector('[data-cart-heading-count]');
    const items = Array.isArray(cart.items) ? cart.items : [];

    if (body) {
      body.innerHTML = items.length
        ? `<div class="cart-dropdown__items" role="list">${items.map(cartItemMarkup).join('')}</div>`
        : cartEmptyMarkup();
    }
    if (footer) footer.hidden = items.length === 0;
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    if (headingCount) headingCount.textContent = cart.item_count > 0 ? `(${cart.item_count})` : '';
  };

  const applyCartState = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.classList.toggle('is-empty', cart.item_count === 0);
    });
    document.querySelectorAll('.cart-link').forEach((link) => {
      const label = link.dataset.cartLabel || 'Shopping bag';
      link.setAttribute('aria-label', `${label}: ${cart.item_count}`);
    });
    renderCartDropdown(cart);
    return cart;
  };

  const refreshCartCount = async ({ showLoading = false } = {}) => {
    if (showLoading) setCartBusy(true);
    try {
      const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Your shopping bag could not be refreshed.');
      const cart = await response.json();
      showCartError('');
      return applyCartState(cart);
    } catch (error) {
      showCartError(error.message || 'Your shopping bag could not be refreshed.');
      return null;
    } finally {
      if (showLoading) setCartBusy(false);
    }
  };

  const changeCartLine = async (lineKey, quantity, focusAction) => {
    if (!lineKey || cartMutationPending) return;
    cartMutationPending = true;
    setCartBusy(true);
    showCartError('');
    const dropdown = getCartDropdown();
    const status = dropdown && dropdown.querySelector('[data-cart-status]');
    try {
      const baseChangeUrl = routes.cartChange || `${routes.cart}/change`;
      const changeUrl = `${baseChangeUrl.replace(/\.js$/, '')}.js`;
      const response = await fetch(changeUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'That quantity could not be updated.');
      applyCartState(cart);
      const message = quantity === 0 ? 'Item removed from your bag.' : `Quantity updated to ${quantity}.`;
      if (status) status.textContent = message;
      announceStatus(message);
      window.requestAnimationFrame(() => {
        if (quantity > 0 && focusAction !== 'remove') {
          const target = Array.from(document.querySelectorAll('[data-cart-quantity-action]')).find((control) => (
            control.dataset.lineKey === lineKey && control.dataset.cartQuantityAction === focusAction
          ));
          if (target) target.focus();
        } else {
          const exitButton = getCartDropdown() && getCartDropdown().querySelector('[data-cart-close]');
          if (exitButton) exitButton.focus();
        }
      });
    } catch (error) {
      showCartError(error.message || 'That change could not be completed.');
      announceStatus(error.message || 'That change could not be completed.');
    } finally {
      cartMutationPending = false;
      setCartBusy(false);
    }
  };

  const addToCart = async (form, button, messageElement) => {
    const originalText = button && button.textContent;
    const originalAriaLabel = button && button.getAttribute('aria-label');
    const addedLabel = (button && button.dataset.addedLabel) || 'Added to bag';
    const addLabel = (button && button.dataset.addLabel) || 'Add to bag';
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    if (messageElement) {
      messageElement.textContent = '';
      messageElement.classList.remove('is-error');
    }
    try {
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'This piece could not be added.');
      await refreshCartCount();
      hideCartToast();
      openCartDropdown(document.querySelector('[data-cart-toggle]'), false);
      announceStatus(`${data.product_title || 'Item'} added to your bag.`);
      if (button) {
        button.classList.add('is-added');
        const text = button.querySelector('[data-add-to-cart-text]');
        if (text) text.textContent = addedLabel;
        else button.setAttribute('aria-label', addedLabel);
      }
      if (messageElement) messageElement.textContent = 'Added to your bag.';
      window.setTimeout(() => {
        if (!button) return;
        button.classList.remove('is-added');
        const text = button.querySelector('[data-add-to-cart-text]');
        if (text) text.textContent = addLabel;
        else if (originalAriaLabel) button.setAttribute('aria-label', originalAriaLabel);
      }, 1800);
    } catch (error) {
      if (messageElement) {
        messageElement.textContent = error.message;
        messageElement.classList.add('is-error');
      } else if (button) {
        announceStatus(error.message);
      }
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (originalAriaLabel) button.setAttribute('aria-label', originalAriaLabel);
        if (originalText && !button.querySelector('[data-add-to-cart-text]')) button.textContent = originalText;
      }
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('form[data-quick-add]')) {
      event.preventDefault();
      addToCart(form, form.querySelector('button[type="submit"]'));
      return;
    }
    if (form.matches('[data-product-form]')) {
      if (event.submitter && event.submitter.name !== 'add') return;
      event.preventDefault();
      const root = form.closest('[data-product-root]');
      const addButton = form.querySelector('[data-add-to-cart]');
      const selectedVariant = form.querySelector('[data-variant-select]');
      if (!addButton || addButton.disabled || !selectedVariant || selectedVariant.disabled || !selectedVariant.value) return;
      addToCart(form, addButton, root && root.querySelector('[data-product-form-message]'));
    }
  });

  const initializeProductGallery = (gallery) => {
    if (!gallery || gallery.dataset.galleryInitialized === 'true') return;
    const viewport = gallery.querySelector('[data-gallery-viewport]');
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const thumbnails = Array.from(gallery.querySelectorAll('[data-gallery-thumbnail]'));
    const previousButton = gallery.querySelector('[data-gallery-previous]');
    const nextButton = gallery.querySelector('[data-gallery-next]');
    const currentLabel = gallery.querySelector('[data-gallery-current]');
    const progress = gallery.querySelector('[data-gallery-progress]');
    if (!viewport || !track || !slides.length) return;

    gallery.dataset.galleryInitialized = 'true';
    let currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let pointerStartX = 0;
    let pointerDeltaX = 0;
    let dragging = false;

    const pauseInactiveMedia = () => {
      slides.forEach((slide, index) => {
        if (index === currentIndex) return;
        slide.querySelectorAll('video').forEach((video) => {
          if (!video.paused) video.pause();
        });
      });
    };

    const goTo = (requestedIndex, options = {}) => {
      const total = slides.length;
      const nextIndex = ((Number(requestedIndex) % total) + total) % total;
      currentIndex = nextIndex;

      if (options.instant) track.style.transition = 'none';
      track.style.transform = `translate3d(-${currentIndex * 100}%, 0, 0)`;
      if (options.instant) {
        track.getBoundingClientRect();
        window.requestAnimationFrame(() => { track.style.transition = ''; });
      }

      slides.forEach((slide, index) => {
        const active = index === currentIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      thumbnails.forEach((thumbnail, index) => {
        const active = index === currentIndex;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active && options.scrollThumbnail !== false) {
          thumbnail.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });

      if (currentLabel) currentLabel.textContent = String(currentIndex + 1);
      if (progress) progress.style.width = `${((currentIndex + 1) / total) * 100}%`;
      pauseInactiveMedia();
      gallery.dispatchEvent(new CustomEvent('montelier:gallery-change', { detail: { index: currentIndex } }));
    };

    gallery._montelierGoTo = goTo;

    if (previousButton) previousButton.addEventListener('click', () => goTo(currentIndex - 1));
    if (nextButton) nextButton.addEventListener('click', () => goTo(currentIndex + 1));
    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => goTo(Number(thumbnail.dataset.galleryIndex)));
    });

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(currentIndex + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        goTo(slides.length - 1);
      }
    });

    viewport.addEventListener('pointerdown', (event) => {
      if (slides.length < 2 || event.button !== 0) return;
      if (event.target.closest('button, video, iframe, model-viewer, a, input, select')) return;
      dragging = true;
      pointerStartX = event.clientX;
      pointerDeltaX = 0;
      track.style.transition = 'none';
      viewport.setPointerCapture(event.pointerId);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      pointerDeltaX = event.clientX - pointerStartX;
      const resistance = (currentIndex === 0 && pointerDeltaX > 0) || (currentIndex === slides.length - 1 && pointerDeltaX < 0) ? 0.35 : 1;
      const adjustedDelta = pointerDeltaX * resistance;
      track.style.transform = `translate3d(calc(-${currentIndex * 100}% + ${adjustedDelta}px), 0, 0)`;
    });

    const finishDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
      track.style.transition = '';
      const threshold = Math.min(80, Math.max(42, viewport.clientWidth * 0.12));
      if (pointerDeltaX <= -threshold) goTo(currentIndex + 1);
      else if (pointerDeltaX >= threshold) goTo(currentIndex - 1);
      else goTo(currentIndex, { scrollThumbnail: false });
      pointerDeltaX = 0;
    };

    viewport.addEventListener('pointerup', finishDrag);
    viewport.addEventListener('pointercancel', finishDrag);
    viewport.addEventListener('lostpointercapture', () => {
      if (!dragging) return;
      dragging = false;
      track.style.transition = '';
      goTo(currentIndex, { scrollThumbnail: false });
    });

    goTo(currentIndex, { instant: true, scrollThumbnail: false });
  };

  const initializeProductGalleries = (root = document) => {
    if (root.matches && root.matches('[data-product-gallery]')) initializeProductGallery(root);
    if (root.querySelectorAll) root.querySelectorAll('[data-product-gallery]').forEach(initializeProductGallery);
  };

  initializeProductGalleries();
  document.addEventListener('shopify:section:load', (event) => initializeProductGalleries(event.target));

  document.addEventListener('change', (event) => {
    const variantSelect = event.target.closest('[data-variant-select]');
    if (!variantSelect || variantSelect.tagName !== 'SELECT') return;
    const root = variantSelect.closest('[data-product-root]');
    if (!root) return;
    const variantData = root.querySelector('[data-product-variants]');
    if (!variantData) return;
    let variants = [];
    try { variants = JSON.parse(variantData.textContent); } catch (_) { return; }

    const variant = variants.find((item) => String(item.id) === variantSelect.value);
    if (!variant) return;
    const addButton = root.querySelector('[data-add-to-cart]');
    const addText = root.querySelector('[data-add-to-cart-text]');
    const priceContainer = root.querySelector('[data-product-price]');
    const sku = root.querySelector('[data-variant-sku]');

    if (addButton) addButton.disabled = !variant.available;
    if (addText) addText.textContent = variant.available
      ? (root.dataset.addToCartLabel || 'Add to bag')
      : (root.dataset.soldOutLabel || 'Sold out');
    if (sku) sku.textContent = variant.sku || '';
    const skuContainer = root.querySelector('[data-variant-sku-container]');
    if (skuContainer) skuContainer.hidden = !variant.sku;
    if (priceContainer) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      priceContainer.innerHTML = `<div class="price product-price${onSale ? ' price--on-sale' : ''}${variant.available ? '' : ' price--sold-out'}"><span class="price__regular">${formatMoney(variant.price)}</span>${onSale ? `<span class="visually-hidden">Regular price</span><s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}</div>`;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
    if (variant.featured_media) {
      const media = root.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (media) {
        const gallery = media.closest('[data-product-gallery]');
        const mediaIndex = Number(media.dataset.galleryIndex || 0);
        if (gallery && typeof gallery._montelierGoTo === 'function') {
          gallery._montelierGoTo(mediaIndex);
        } else if (window.innerWidth < 750) {
          media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
      }
    }
  });

  // Relevance-first search page with lightweight synonym normalization and
  // Shopify predictive product results. The normalized query helps common
  // shopper phrases such as “men's watches” match product types and tags.
  const normalizeProductSearchQuery = (value) => {
    const aliases = [
      [/\bmen(?:['’]?s)?\b/gi, 'men'],
      [/\bgents?\b/gi, 'men'],
      [/\bwomen(?:['’]?s)?\b/gi, 'women'],
      [/\bladies\b/gi, 'women'],
      [/\bwatches\b/gi, 'watch'],
      [/\btimepieces\b/gi, 'watch'],
      [/\bjewellery\b/gi, 'jewelry'],
      [/\baccessories\b/gi, 'accessory'],
      [/\bbracelets\b/gi, 'bracelet'],
      [/\bnecklaces\b/gi, 'necklace'],
      [/\brings\b/gi, 'ring']
    ];

    let query = String(value || '')
      .trim()
      .replace(/[&/+_,]+/g, ' ')
      .replace(/[“”"()\[\]{}:;!?]+/g, ' ');

    aliases.forEach(([pattern, replacement]) => {
      query = query.replace(pattern, replacement);
    });

    const seen = new Set();
    return query
      .split(/\s+/)
      .filter(Boolean)
      .filter((token) => {
        const key = token.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .join(' ')
      .trim();
  };

  let searchPageTimer = null;
  let searchPageController = null;

  const renderSearchPagePredictive = (container, products, originalQuery, normalizedQuery) => {
    if (!container) return;

    if (!products.length) {
      container.hidden = false;
      container.innerHTML = `
        <div class="search-page__predictive-message">
          No exact products found for <strong>${escapeHtml(originalQuery)}</strong>.<br>
          Try a shorter phrase, color, product type or collection.
        </div>
      `;
      return;
    }

    const items = products.map((product, index) => {
      const imageUrl = product.featured_image && product.featured_image.url
        ? `${product.featured_image.url}${product.featured_image.url.includes('?') ? '&' : '?'}width=160`
        : '';
      const image = imageUrl
        ? `<img class="search-page__predictive-image" src="${escapeHtml(imageUrl)}" alt="" width="66" height="66" loading="lazy">`
        : '<span class="search-page__predictive-image" aria-hidden="true"></span>';
      const rawPrice = Number(product.price);
      const price = Number.isFinite(rawPrice) ? formatMoney(Math.round(rawPrice * 100)) : '';

      return `
        <a class="search-page__predictive-item" href="${escapeHtml(product.url)}">
          ${index === 0 ? '<span class="search-page__best-match">Best match</span>' : ''}
          ${image}
          <span class="search-page__predictive-copy">
            <strong>${escapeHtml(product.title)}</strong>
            ${price ? `<span>${price}</span>` : ''}
          </span>
          <span class="search-page__predictive-arrow" aria-hidden="true">&#8599;</span>
        </a>
      `;
    }).join('');

    const queryParams = new URLSearchParams({
      q: normalizedQuery,
      type: 'product',
      'options[prefix]': 'last',
      'options[unavailable_products]': 'last',
      sort_by: 'relevance'
    });
    const allResultsUrl = `${routes.search}?${queryParams.toString()}`;
    const smartTerm = normalizedQuery.toLowerCase() !== originalQuery.trim().toLowerCase()
      ? `<span class="search-page__smart-term">Smart match: ${escapeHtml(normalizedQuery)}</span>`
      : '';

    container.hidden = false;
    container.innerHTML = `
      <div class="search-page__predictive-head">
        <span><strong>${products.length}</strong> suggested ${products.length === 1 ? 'piece' : 'pieces'}</span>
        ${smartTerm}
      </div>
      <div class="search-page__predictive-list">${items}</div>
      <div class="search-page__predictive-footer">
        <p>Products are ordered by Shopify relevance, availability and term match.</p>
        <a class="search-page__predictive-all" href="${escapeHtml(allResultsUrl)}">View all results <span aria-hidden="true">&#8599;</span></a>
      </div>
    `;
  };

  const runSearchPagePredictive = (root, immediate = false) => {
    if (!root) return;
    const input = root.querySelector('[data-search-page-input]');
    const container = root.querySelector('[data-search-page-predictive]');
    const clear = root.querySelector('[data-search-page-clear]');
    if (!input || !container) return;

    const originalQuery = input.value.trim();
    const normalizedQuery = normalizeProductSearchQuery(originalQuery);
    if (clear) clear.hidden = !originalQuery;
    window.clearTimeout(searchPageTimer);

    if (normalizedQuery.length < 2) {
      if (searchPageController) searchPageController.abort();
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    const perform = async () => {
      if (searchPageController) searchPageController.abort();
      searchPageController = new AbortController();
      container.hidden = false;
      container.innerHTML = `
        <div class="search-page__predictive-loading" aria-label="Searching">
          <span></span><span></span><span></span><span></span>
        </div>
      `;

      try {
        const buildUrl = (query) => `${routes.predictiveSearch}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
        let response = await fetch(buildUrl(normalizedQuery), {
          signal: searchPageController.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Search unavailable');
        let data = await response.json();
        let products = data.resources && data.resources.results ? data.resources.results.products || [] : [];

        // If normalization was too strict for an unusual product name, retry the
        // shopper's original phrase before showing an empty state.
        if (!products.length && normalizedQuery.toLowerCase() !== originalQuery.toLowerCase()) {
          response = await fetch(buildUrl(originalQuery), {
            signal: searchPageController.signal,
            headers: { Accept: 'application/json' }
          });
          if (response.ok) {
            data = await response.json();
            products = data.resources && data.resources.results ? data.resources.results.products || [] : [];
          }
        }

        renderSearchPagePredictive(container, products, originalQuery, normalizedQuery);
      } catch (error) {
        if (error.name === 'AbortError') return;
        container.hidden = false;
        container.innerHTML = '<div class="search-page__predictive-message">Search is temporarily unavailable. You can still press Enter to see complete results.</div>';
      }
    };

    if (immediate) perform();
    else searchPageTimer = window.setTimeout(perform, 180);
  };

  const initializeSearchPage = (root) => {
    if (!root || root.dataset.searchPageInitialized === 'true') return;
    root.dataset.searchPageInitialized = 'true';

    const form = root.querySelector('[data-search-page-form]');
    const input = root.querySelector('[data-search-page-input]');
    const clear = root.querySelector('[data-search-page-clear]');
    const container = root.querySelector('[data-search-page-predictive]');
    if (!form || !input) return;

    input.addEventListener('input', () => runSearchPagePredictive(root));
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2) runSearchPagePredictive(root, true);
    });

    clear && clear.addEventListener('click', () => {
      if (searchPageController) searchPageController.abort();
      window.clearTimeout(searchPageTimer);
      input.value = '';
      clear.hidden = true;
      if (container) {
        container.hidden = true;
        container.innerHTML = '';
      }
      input.focus({ preventScroll: true });
    });

    form.addEventListener('submit', (event) => {
      const normalizedQuery = normalizeProductSearchQuery(input.value);
      if (!normalizedQuery) {
        event.preventDefault();
        input.focus({ preventScroll: true });
        return;
      }
      input.value = normalizedQuery;
      saveRecentSearch(normalizedQuery);
    });

    root.querySelectorAll('[data-search-page-shortcut]').forEach((button) => {
      button.addEventListener('click', () => {
        input.value = button.dataset.searchPageShortcut || button.textContent.trim();
        form.requestSubmit();
      });
    });

    const refocus = root.querySelector('[data-search-page-refocus]');
    refocus && refocus.addEventListener('click', () => {
      input.focus({ preventScroll: false });
      input.select();
      window.scrollTo({ top: Math.max(0, input.getBoundingClientRect().top + window.scrollY - 150), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !container || container.hidden) return;
      container.hidden = true;
      input.focus({ preventScroll: true });
    });

    document.addEventListener('click', (event) => {
      if (!container || container.hidden || root.contains(event.target)) return;
      container.hidden = true;
    });
  };

  const initializeSearchPages = (root = document) => {
    if (root.matches && root.matches('[data-search-page]')) initializeSearchPage(root);
    if (root.querySelectorAll) root.querySelectorAll('[data-search-page]').forEach(initializeSearchPage);
  };

  initializeSearchPages();
  document.addEventListener('shopify:section:load', (event) => initializeSearchPages(event.target));

  // Judge.me can be rendered inside a closed details panel. Trigger a fresh
  // layout pass when the customer opens it so carousels, forms and review
  // widths calculate correctly without changing any Judge.me data.
  document.addEventListener('toggle', (event) => {
    const reviewPanel = event.target.closest && event.target.closest('[data-judgeme-review-panel]');
    if (!reviewPanel || !reviewPanel.open) return;
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, true);

})();


/* === Montelier-controlled Judge.me reviews === */
(() => {
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const stripInlineLayout = (root) => {
    if (!root) return;
    [root, ...root.querySelectorAll('*')].forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      element.style.removeProperty('width');
      element.style.removeProperty('min-width');
      element.style.removeProperty('max-width');
      element.style.removeProperty('height');
      element.style.removeProperty('min-height');
      element.style.removeProperty('transform');
      element.style.removeProperty('left');
      element.style.removeProperty('right');
      element.style.removeProperty('position');
      element.removeAttribute('aria-hidden');
      element.removeAttribute('tabindex');
    });
  };

  const reviewSignature = (item) => [
    item.querySelector('.jdgm-carousel-item__review-body')?.textContent || '',
    item.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent || '',
    item.querySelector('.jdgm-carousel-item__product-title')?.textContent || ''
  ].join('|').trim().replace(/\s+/g, ' ');

  const normalizeReviewImageUrl = (value) => {
    if (!value) return '';
    let url = String(value).trim();
    if (!url || url === 'about:blank' || url.startsWith('data:image/gif')) return '';
    if (url.startsWith('//')) url = `${window.location.protocol}${url}`;
    return url;
  };

  const firstSrcsetUrl = (value) => {
    if (!value) return '';
    const first = String(value).split(',')[0]?.trim().split(/\s+/)[0] || '';
    return normalizeReviewImageUrl(first);
  };

  const reviewProductElements = (card) => {
    const product = card?.querySelector('.jdgm-carousel-item__product');
    if (!product) return { product: null, media: null, image: null };

    let image = product.querySelector('img.jdgm-carousel-item__product-image, .jdgm-carousel-item__product-image img, img');
    let media = image?.closest('.jdgm-carousel-item__product-image');

    if (image && media === image) {
      const wrapper = document.createElement('span');
      wrapper.className = 'montelier-review-product-media';
      image.parentNode?.insertBefore(wrapper, image);
      wrapper.appendChild(image);
      media = wrapper;
    } else if (media) {
      media.classList.add('montelier-review-product-media');
    } else if (image) {
      const wrapper = document.createElement('span');
      wrapper.className = 'montelier-review-product-media';
      image.parentNode?.insertBefore(wrapper, image);
      wrapper.appendChild(image);
      media = wrapper;
    }

    if (image) image.classList.add('montelier-review-product-image');
    return { product, media, image };
  };

  const testReviewImageUrl = (url) => new Promise((resolve) => {
    const cleanUrl = normalizeReviewImageUrl(url);
    if (!cleanUrl) return resolve('');
    const probe = new Image();
    const timer = window.setTimeout(() => resolve(''), 3500);
    probe.onload = () => {
      window.clearTimeout(timer);
      resolve(probe.naturalWidth > 1 ? cleanUrl : '');
    };
    probe.onerror = () => {
      window.clearTimeout(timer);
      resolve('');
    };
    probe.src = cleanUrl;
  });

  const setReviewProductImage = (card, image, media, url) => {
    const cleanUrl = normalizeReviewImageUrl(url);
    if (!image || !cleanUrl) return false;
    image.hidden = false;
    image.removeAttribute('srcset');
    image.removeAttribute('data-srcset');
    image.src = cleanUrl;
    image.loading = 'eager';
    image.decoding = 'async';
    media?.classList.remove('is-missing-image');
    media?.querySelector('.montelier-review-product-placeholder')?.remove();
    card.classList.remove('is-review-image-missing');
    return true;
  };

  const showReviewImagePlaceholder = (card, media) => {
    if (!media || media.querySelector('.montelier-review-product-placeholder')) return;
    media.classList.add('is-missing-image');
    card.classList.add('is-review-image-missing');
    const placeholder = document.createElement('span');
    placeholder.className = 'montelier-review-product-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.textContent = 'M';
    media.appendChild(placeholder);
  };

  const fetchShopifyReviewProductImage = async (card) => {
    const productTitle = card.querySelector('.jdgm-carousel-item__product-title')?.textContent?.trim() || '';
    const productLink = card.querySelector(
      '.jdgm-carousel-item__product a[href*="/products/"], a.jdgm-carousel-item__product-title[href*="/products/"], .jdgm-carousel-item__product-title a[href*="/products/"]'
    );

    if (productLink?.href) {
      try {
        const productUrl = new URL(productLink.href, window.location.origin);
        if (productUrl.origin === window.location.origin && productUrl.pathname.includes('/products/')) {
          const response = await fetch(`${productUrl.pathname}.js`, { credentials: 'same-origin' });
          if (response.ok) {
            const product = await response.json();
            const image = product.featured_image || product.images?.[0];
            if (image) return normalizeReviewImageUrl(typeof image === 'string' ? image : image.src || image.url);
          }
        }
      } catch (_) {
        // Fall through to Shopify predictive search by product title.
      }
    }

    if (!productTitle) return '';
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(productTitle)}&resources[type]=product&resources[limit]=1`, { credentials: 'same-origin' });
      if (!response.ok) return '';
      const data = await response.json();
      const product = data?.resources?.results?.products?.[0];
      const image = product?.featured_image?.url || product?.featured_image || product?.image?.url || product?.image;
      return normalizeReviewImageUrl(image);
    } catch (_) {
      return '';
    }
  };

  const hydrateReviewProductImage = async (card) => {
    if (!card || card.dataset.montelierImageHydrated === 'true') return;
    card.dataset.montelierImageHydrated = 'true';
    const { media, image } = reviewProductElements(card);
    if (!image) return;

    const candidates = [
      image.getAttribute('data-src'),
      image.getAttribute('data-lazy-src'),
      image.getAttribute('data-original'),
      firstSrcsetUrl(image.getAttribute('data-srcset')),
      firstSrcsetUrl(image.getAttribute('srcset')),
      image.getAttribute('src')
    ].map(normalizeReviewImageUrl).filter(Boolean);

    for (const candidate of [...new Set(candidates)]) {
      const working = await testReviewImageUrl(candidate);
      if (working) {
        setReviewProductImage(card, image, media, working);
        return;
      }
    }

    const fallback = await fetchShopifyReviewProductImage(card);
    const workingFallback = await testReviewImageUrl(fallback);
    if (workingFallback) {
      setReviewProductImage(card, image, media, workingFallback);
      return;
    }

    image.hidden = true;
    showReviewImagePlaceholder(card, media);
  };

  const collectReviewItems = (wrapper) => {
    const source = wrapper?.querySelector('[data-montelier-judge-source]');
    if (!source) return 0;
    wrapper._montelierReviewCache ||= new Map();

    source.querySelectorAll('.jdgm-carousel-item').forEach((item) => {
      const signature = reviewSignature(item);
      if (!signature || wrapper._montelierReviewCache.has(signature)) return;
      const clone = item.cloneNode(true);
      stripInlineLayout(clone);
      clone.classList.add('montelier-review-card');
      clone.dataset.montelierReviewSignature = signature;
      wrapper._montelierReviewCache.set(signature, clone);
    });

    return wrapper._montelierReviewCache.size;
  };

  const perViewForWidth = () => {
    if (window.matchMedia('(max-width: 749px)').matches) return 1;
    if (window.matchMedia('(max-width: 1099px)').matches) return 2;
    return 3;
  };

  const getReviewScrollerParts = (wrapper) => ({
    source: wrapper?.querySelector('[data-montelier-judge-source]'),
    carousel: wrapper?.querySelector('[data-montelier-review-carousel]'),
    viewport: wrapper?.querySelector('.montelier-review-carousel__viewport'),
    track: wrapper?.querySelector('[data-montelier-review-track]'),
    status: wrapper?.querySelector('[data-montelier-review-status]'),
    nav: wrapper?.querySelector('[data-montelier-review-nav]'),
    previous: wrapper?.querySelector('[data-montelier-review-previous]'),
    next: wrapper?.querySelector('[data-montelier-review-next]')
  });

  const sizeReviewCards = (wrapper) => {
    const { viewport } = getReviewScrollerParts(wrapper);
    if (!viewport) return;
    const perView = perViewForWidth();
    const gap = window.matchMedia('(max-width: 749px)').matches ? 14 : 20;
    const width = Math.max(260, (viewport.clientWidth - gap * (perView - 1)) / perView);
    wrapper.style.setProperty('--montelier-review-card-width', `${width}px`);
    wrapper.style.setProperty('--montelier-review-gap', `${gap}px`);
    wrapper.dataset.montelierReviewsPerView = String(perView);
  };

  const updateReviewScroller = (wrapper, announce = false) => {
    const { viewport, track, status, nav, previous, next } = getReviewScrollerParts(wrapper);
    if (!viewport || !track || !status || !nav || !previous || !next) return;
    const cards = [...track.querySelectorAll('.montelier-review-card')];
    const perView = Number(wrapper.dataset.montelierReviewsPerView || perViewForWidth());
    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const canScroll = maxScroll > 4 && cards.length > perView;
    const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 0;
    const step = cards[0] ? cards[0].getBoundingClientRect().width + gap : viewport.clientWidth;
    const firstIndex = step > 0 ? Math.min(cards.length - 1, Math.max(0, Math.round(viewport.scrollLeft / step))) : 0;
    const lastIndex = Math.min(cards.length, firstIndex + perView);

    previous.disabled = !canScroll || viewport.scrollLeft <= 4;
    next.disabled = !canScroll || viewport.scrollLeft >= maxScroll - 4;
    nav.hidden = !canScroll;
    status.textContent = cards.length
      ? (canScroll ? `Showing reviews ${firstIndex + 1}–${lastIndex} of ${cards.length}` : `${cards.length} ${cards.length === 1 ? 'review' : 'reviews'}`)
      : '';
    if (announce) status.setAttribute('aria-live', 'polite');
  };

  const appendCollectedReviews = (wrapper) => {
    const { carousel, track } = getReviewScrollerParts(wrapper);
    if (!carousel || !track || !wrapper._montelierReviewCache?.size) return false;
    const existing = new Set([...track.querySelectorAll('[data-montelier-review-signature]')].map((card) => card.dataset.montelierReviewSignature));
    wrapper._montelierReviewCache.forEach((cached, signature) => {
      if (existing.has(signature)) return;
      const card = cached.cloneNode(true);
      stripInlineLayout(card);
      card.classList.add('montelier-review-card');
      card.dataset.montelierReviewSignature = signature;
      track.appendChild(card);
      hydrateReviewProductImage(card);
    });
    carousel.hidden = false;
    wrapper.classList.add('is-montelier-ready');
    sizeReviewCards(wrapper);
    window.requestAnimationFrame(() => updateReviewScroller(wrapper, false));
    return true;
  };

  const harvestJudgeMeReviews = (wrapper) => {
    if (!wrapper || wrapper.dataset.montelierHarvesting === 'true') return;
    wrapper.dataset.montelierHarvesting = 'true';
    const { source } = getReviewScrollerParts(wrapper);
    if (!source) return;
    const expected = Number(wrapper.dataset.totalReviews || 0);
    let attempts = 0;
    let stablePasses = 0;
    let previousCount = collectReviewItems(wrapper);
    appendCollectedReviews(wrapper);

    const tick = () => {
      const count = collectReviewItems(wrapper);
      appendCollectedReviews(wrapper);
      stablePasses = count === previousCount ? stablePasses + 1 : 0;
      previousCount = count;
      attempts += 1;

      if ((expected > 0 && count >= expected) || attempts >= Math.max(12, expected * 2) || stablePasses >= 5) {
        wrapper.dataset.montelierHarvesting = 'complete';
        return;
      }

      const nativeNext = source.querySelector('.jdgm-carousel__right-arrow, .jdgm-carousel__next, [aria-label*="Next"]');
      if (nativeNext instanceof HTMLElement) nativeNext.click();
      window.setTimeout(tick, 420);
    };

    window.setTimeout(tick, 350);
  };

  const initializeReviewCarousel = (wrapper) => {
    if (!wrapper || wrapper.dataset.montelierReviewObserver === 'true') return;
    wrapper.dataset.montelierReviewObserver = 'true';
    const { source, viewport, previous, next } = getReviewScrollerParts(wrapper);
    if (!source || !viewport || !previous || !next) return;

    collectReviewItems(wrapper);
    appendCollectedReviews(wrapper);
    harvestJudgeMeReviews(wrapper);

    const observer = new MutationObserver(() => {
      const before = wrapper._montelierReviewCache?.size || 0;
      const after = collectReviewItems(wrapper);
      if (after > before) appendCollectedReviews(wrapper);
    });
    observer.observe(source, { childList: true, subtree: true, characterData: true });

    const scrollOne = (direction) => {
      const firstCard = viewport.querySelector('.montelier-review-card');
      const track = wrapper.querySelector('[data-montelier-review-track]');
      const gap = track ? (parseFloat(getComputedStyle(track).gap) || 0) : 0;
      const distance = firstCard ? firstCard.getBoundingClientRect().width + gap : viewport.clientWidth * 0.85;
      viewport.scrollBy({ left: direction * distance, behavior: reducedMotionQuery.matches ? 'auto' : 'smooth' });
    };
    previous.addEventListener('click', () => scrollOne(-1));
    next.addEventListener('click', () => scrollOne(1));
    viewport.addEventListener('scroll', () => updateReviewScroller(wrapper), { passive: true });

    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      dragging = true;
      startX = event.clientX;
      startScroll = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
      viewport.setPointerCapture?.(event.pointerId);
    });
    viewport.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      viewport.scrollLeft = startScroll - (event.clientX - startX);
    });
    const stopDragging = (event) => {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      if (event?.pointerId !== undefined) viewport.releasePointerCapture?.(event.pointerId);
    };
    viewport.addEventListener('pointerup', stopDragging);
    viewport.addEventListener('pointercancel', stopDragging);
    viewport.addEventListener('pointerleave', (event) => { if (dragging && event.pointerType === 'mouse') stopDragging(event); });
  };

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      document.querySelectorAll('[data-montelier-judge-carousel]').forEach((wrapper) => {
        const { viewport } = getReviewScrollerParts(wrapper);
        const ratio = viewport && viewport.scrollWidth > viewport.clientWidth ? viewport.scrollLeft / (viewport.scrollWidth - viewport.clientWidth) : 0;
        sizeReviewCards(wrapper);
        window.requestAnimationFrame(() => {
          if (viewport) viewport.scrollLeft = ratio * Math.max(0, viewport.scrollWidth - viewport.clientWidth);
          updateReviewScroller(wrapper, false);
        });
      });
    }, 120);
  });

  const injectJudgeMeTheme = () => {
    if (document.getElementById('MontelierJudgeMeTheme')) return;
    const style = document.createElement('style');
    style.id = 'MontelierJudgeMeTheme';
    style.textContent = `
      #judgeme_product_reviews .jdgm-rev-widg__title { display: none !important; }
      .product-reviews-panel #judgeme_product_reviews,
      .product-reviews-panel #judgeme_product_reviews .jdgm-rev-widg,
      .product-reviews-panel #judgeme_product_reviews .jdgm-rev-widg__body {
        width: 100% !important; max-width: none !important; margin: 0 !important;
        padding: 0 !important; border: 0 !important; background: transparent !important;
        color: #0b2238 !important;
      }
      .product-reviews-panel #judgeme_product_reviews .jdgm-form-wrapper {
        display: block; margin: 18px 0 0 !important; padding: 24px !important;
        border: 1px solid rgba(202,171,111,.38) !important; border-radius: 24px !important;
        background: linear-gradient(180deg, rgba(255,255,255,.96), rgba(246,242,232,.94)) !important;
        box-shadow: 0 22px 54px rgba(11,34,56,.09) !important;
      }
      .product-reviews-panel #judgeme_product_reviews .jdgm-form__title,
      .product-reviews-panel #judgeme_product_reviews .jdgm-form__reviewer-name-format-container,
      .product-reviews-panel #judgeme_product_reviews label {
        color: #0b2238 !important;
      }
      .product-reviews-panel #judgeme_product_reviews .jdgm-form__title {
        margin: 0 0 22px !important; font-family: var(--font-heading) !important;
        font-size: clamp(1.8rem,3vw,2.7rem) !important; font-weight: 400 !important;
        letter-spacing: -.035em !important;
      }
      .product-reviews-panel #judgeme_product_reviews .jdgm-form__fieldset {
        margin: 0 0 18px !important; padding: 0 !important; border: 0 !important;
      }
      .product-reviews-panel #judgeme_product_reviews input,
      .product-reviews-panel #judgeme_product_reviews textarea,
      .product-reviews-panel #judgeme_product_reviews select {
        box-sizing: border-box !important; width: 100% !important; min-height: 52px !important;
        padding: 13px 15px !important; border: 1px solid rgba(11,34,56,.16) !important;
        border-radius: 15px !important; background: rgba(255,255,255,.96) !important;
        color: #0b2238 !important; box-shadow: inset 0 1px 2px rgba(11,34,56,.035) !important;
        outline: none !important; transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease !important;
      }
      .product-reviews-panel #judgeme_product_reviews textarea { min-height: 150px !important; resize: vertical !important; }
      .product-reviews-panel #judgeme_product_reviews input:focus,
      .product-reviews-panel #judgeme_product_reviews textarea:focus,
      .product-reviews-panel #judgeme_product_reviews select:focus {
        border-color: #caab6f !important; box-shadow: 0 0 0 4px rgba(202,171,111,.16) !important;
      }
      .product-reviews-panel #judgeme_product_reviews .jdgm-form__rating .jdgm-star,
      .product-reviews-panel #judgeme_product_reviews .jdgm-star { color: #caab6f !important; }
      .product-reviews-panel #judgeme_product_reviews .jdgm-picture-fieldset__box,
      .product-reviews-panel #judgeme_product_reviews .jdgm-media-fieldset__box {
        min-height: 108px !important; border: 1px dashed rgba(202,171,111,.66) !important;
        border-radius: 18px !important; background: rgba(202,171,111,.075) !important;
        color: #0b2238 !important;
      }
      .product-reviews-panel #judgeme_product_reviews .jdgm-submit-rev,
      .product-reviews-panel #judgeme_product_reviews .jdgm-cancel-rev,
      .product-reviews-panel #judgeme_product_reviews .jdgm-write-rev-link,
      .product-reviews-panel #judgeme_product_reviews .jdgm-btn {
        display: inline-flex !important; min-height: 50px !important; align-items: center !important;
        justify-content: center !important; padding: 13px 24px !important; border-radius: 999px !important;
        font-size: .74rem !important; font-weight: 700 !important; letter-spacing: .12em !important;
        text-transform: uppercase !important; text-decoration: none !important;
        transition: transform .22s ease, box-shadow .22s ease, background .22s ease !important;
      }
      .product-reviews-panel #judgeme_product_reviews .jdgm-submit-rev,
      .product-reviews-panel #judgeme_product_reviews .jdgm-write-rev-link,
      .product-reviews-panel #judgeme_product_reviews .jdgm-btn {
        border: 1px solid #caab6f !important; background: #caab6f !important;
        color: #0b2238 !important; box-shadow: 0 14px 30px rgba(202,171,111,.24) !important;
      }
      .product-reviews-panel #judgeme_product_reviews .jdgm-cancel-rev {
        border: 1px solid rgba(11,34,56,.18) !important; background: #f6f2e8 !important;
        color: #0b2238 !important;
      }
      .product-reviews-panel #judgeme_product_reviews .jdgm-submit-rev:hover,
      .product-reviews-panel #judgeme_product_reviews .jdgm-write-rev-link:hover,
      .product-reviews-panel #judgeme_product_reviews .jdgm-btn:hover {
        transform: translateY(-2px) !important; background: #d8bd86 !important;
        box-shadow: 0 18px 38px rgba(202,171,111,.3) !important;
      }
      .product-reviews-panel #judgeme_product_reviews .jdgm-cancel-rev:hover {
        transform: translateY(-2px) !important; border-color: #caab6f !important;
      }
      .product-reviews-panel.is-empty #judgeme_product_reviews .jdgm-rev-widg__summary,
      .product-reviews-panel.is-empty #judgeme_product_reviews .jdgm-widget-actions-wrapper,
      .product-reviews-panel.is-empty #judgeme_product_reviews .jdgm-rev-widg__reviews { display: none !important; }
      .product-reviews-panel.is-writing-review .montelier-product-review-empty { display: none !important; }
      .product-reviews-panel #judgeme_product_reviews [data-montelier-hide] { display: none !important; }
    `;
    document.head.appendChild(style);
  };

  const isVisible = (element) => Boolean(element && (element.offsetWidth || element.offsetHeight || element.getClientRects().length));

  const forceJudgeMeFormTheme = (widget) => {
    if (!widget) return;
    const important = (element, property, value) => element?.style?.setProperty(property, value, 'important');

    widget.querySelectorAll('.jdgm-write-rev-link, [data-dynamic-form="review"]').forEach((button) => {
      important(button, 'position', 'absolute');
      important(button, 'width', '1px');
      important(button, 'height', '1px');
      important(button, 'overflow', 'hidden');
      important(button, 'clip-path', 'inset(50%)');
      important(button, 'opacity', '0');
      important(button, 'pointer-events', 'none');
    });

    widget.querySelectorAll('.jdgm-form-wrapper, .jdgm-form').forEach((form) => {
      important(form, 'background', '#f7f3e9');
      important(form, 'border', '1px solid rgba(202,171,111,.42)');
      important(form, 'border-radius', '24px');
      important(form, 'padding', '24px');
      important(form, 'box-shadow', '0 22px 54px rgba(11,34,56,.09)');
    });

    widget.querySelectorAll('input, textarea, select').forEach((field) => {
      important(field, 'background', '#fffdf8');
      important(field, 'color', '#0b2238');
      important(field, 'border', '1px solid rgba(11,34,56,.16)');
      important(field, 'border-radius', '15px');
      important(field, 'box-shadow', 'inset 0 1px 2px rgba(11,34,56,.035)');
    });

    widget.querySelectorAll('.jdgm-submit-rev, .jdgm-btn:not(.jdgm-write-rev-link)').forEach((button) => {
      important(button, 'background', '#0b2238');
      important(button, 'color', '#f7f3e9');
      important(button, 'border', '1px solid #0b2238');
      important(button, 'border-radius', '999px');
      important(button, 'box-shadow', '0 14px 30px rgba(11,34,56,.16)');
    });

    widget.querySelectorAll('.jdgm-cancel-rev').forEach((button) => {
      important(button, 'background', '#f7f3e9');
      important(button, 'color', '#0b2238');
      important(button, 'border', '1px solid rgba(202,171,111,.55)');
      important(button, 'border-radius', '999px');
    });

    widget.querySelectorAll('.jdgm-star').forEach((star) => important(star, 'color', '#caab6f'));
  };

  const enhanceProductReviewPanel = (panel) => {
    if (!panel) return;
    const widget = panel.querySelector('#judgeme_product_reviews, .jdgm-review-widget');
    if (!widget) return;

    panel.querySelectorAll('.jdgm-rev-widg__title').forEach((title) => title.setAttribute('data-montelier-hide', 'true'));
    panel.querySelectorAll('*').forEach((element) => {
      if (element.children.length) return;
      const value = element.textContent.trim().toLowerCase();
      if (value === 'no items found' || value === 'be the first to write a review') element.setAttribute('data-montelier-hide', 'true');
    });

    forceJudgeMeFormTheme(widget);
    const reviews = widget.querySelectorAll('.jdgm-rev');
    const form = widget.querySelector('.jdgm-form-wrapper, .jdgm-form');
    const writing = isVisible(form);
    panel.classList.toggle('is-writing-review', writing);
    panel.classList.toggle('is-empty', reviews.length === 0);

    const customButton = panel.querySelector('[data-montelier-write-review]');
    if (customButton && customButton.dataset.montelierBound !== 'true') {
      customButton.dataset.montelierBound = 'true';
      customButton.addEventListener('click', () => {
        const tryOpen = (attempt = 0) => {
          const nativeButton = widget.querySelector('.jdgm-write-rev-link, [data-dynamic-form="review"]');
          if (nativeButton) {
            nativeButton.click();
            window.setTimeout(() => {
              enhanceProductReviewPanel(panel);
              const firstInput = widget.querySelector('.jdgm-form-wrapper input, .jdgm-form-wrapper textarea, .jdgm-form input, .jdgm-form textarea');
              firstInput?.focus({ preventScroll: true });
            }, 180);
            return;
          }
          if (attempt < 10) window.setTimeout(() => tryOpen(attempt + 1), 150);
        };
        tryOpen();
      });
    }
  };

  const initializeProductReviewPanel = (panel) => {
    if (!panel || panel.dataset.montelierJudgeObserver === 'true') return;
    panel.dataset.montelierJudgeObserver = 'true';
    enhanceProductReviewPanel(panel);
    const observer = new MutationObserver(() => enhanceProductReviewPanel(panel));
    observer.observe(panel, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
  };

  const initializeJudgeMe = (root = document) => {
    injectJudgeMeTheme();
    if (root.matches?.('[data-montelier-judge-carousel]')) initializeReviewCarousel(root);
    root.querySelectorAll?.('[data-montelier-judge-carousel]').forEach(initializeReviewCarousel);
    if (root.matches?.('.product-reviews-panel')) initializeProductReviewPanel(root);
    root.querySelectorAll?.('.product-reviews-panel').forEach(initializeProductReviewPanel);
  };

  const start = () => initializeJudgeMe(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('load', () => initializeJudgeMe(document), { once: true });
  document.addEventListener('shopify:section:load', (event) => initializeJudgeMe(event.target));
})();
